import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { Chapter, Prose, TheoryBox, MTQuote } from "@/components/mt/MTBox";
import { NoteImage } from "@/components/mt/NoteImage";
import { YouTubeEmbed } from "@/components/song/YouTubeEmbed";
import { RomanNumeralAnalysis } from "@/components/song/RomanNumeralAnalysis";

export default function Beatles8() {
  const { lesson, course, accent } = findLesson("the-beatles", "8");
  return (
    <LessonShell
      course={course}
      courseSlug="the-beatles"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <Chapter eyebrow="First listen" title="High information music">
        <Prose>
          <p>
            Before doing any theory, Jason plays "In My Life" and asks one question: what are all
            the elements? This first-listen approach — hearing everything before analysing anything
            — is the foundation of how he teaches transcription throughout the Beatles course.
          </p>
          <p>
            "In My Life" is what Jason calls <strong>high information music</strong>. "There are a
            lot of ingredients, lot of elements, and it's not lot of sounds — lot of parts. They're
            playing many parts in parallel." The old-school Beatles recording technique helps here:
            the instruments are panned to one side and the vocals to the other. Jason calls this
            the old-school way of mixing from the 1960s, and it makes the song unusually easy to
            separate by ear.
          </p>
          <p>
            The song is in A major. Before touching the piano, Jason has students confirm this:
            find the note that feels like home, then check the third (C♯) and fifth (E) as well,
            to make sure it really is A major. Once the key is confirmed, the listening can begin
            in earnest.
          </p>
        </Prose>

        <TheoryBox label="How to confirm a key by ear">
          Sing what feels like the root of the song — the note that sounds like "home." Then play
          the 1, 3, and 5 of that note on the keyboard. If all three sound right against the track,
          you've confirmed the key. In A major: A (root), C♯ (third), E (fifth). "So if you feel
          the scale is A, don't just stop there. Also play the 5th which is E and the 3rd which is
          what? C sharp. So is it A? Just check and confirm." For "In My Life," A major confirms
          immediately.
        </TheoryBox>
      </Chapter>

      <YouTubeEmbed
        videoId="YBcdt6DsLQA"
        title="In My Life — The Beatles"
        caption="Listen for the two separate melodies in the intro: one guitar going high (C♯ A C♯ D E G♯), one bass going lower. Then listen for when the drum changes from hi-hat to tambourine to ride cymbal. The piano solo is the instrumental break in the middle of the song."
      />

      <Chapter eyebrow="The intro" title="Two melodies — left channel and right channel">
        <Prose>
          <p>
            The intro of "In My Life" carries two separate melodic lines at the same time. On the
            left channel: the guitar (or bass) plays a higher-register melody. The notes are
            C♯ – A – C♯ – D – E – G♯. In solfège terms, Jason describes this as
            "sa, saagamapa, pani saa" — a high A as the sa, then the scale going up. This line
            loops and repeats throughout the intro.
          </p>
          <p>
            Below that, on the bass: a separate melodic shape that holds on an A and then moves.
            "What he's doing is he holds on an A... and then wait." The two lines create a
            counterpoint texture — two distinct tunes, each with its own rhythm and contour,
            sounding simultaneously.
          </p>
          <p>
            For students: "I hope all of you heard both those tunes. If you did, that is a very
            good step forward. If you're not exactly sure of the notes, that's fine. At least when
            you're listening to songs you should hear every single ingredient. You will appreciate
            the music also a lot better."
          </p>
        </Prose>

        <MTQuote>
          "This is what I would call high information music. So there are lot of ingredients,
          lot of elements, and it's not lot of sounds, lot of parts. They're playing many parts
          in parallel."
        </MTQuote>
      </Chapter>

      <Chapter eyebrow="The drums" title="Hi-hat to tambourine to ride cymbal">
        <Prose>
          <p>
            One of the listening exercises Jason runs in this class is tracking how the drums
            change across the song's sections. This isn't a trivial observation — the drum choices
            are deliberate compositional decisions that control the energy level and texture at
            each point.
          </p>
          <p>
            The song opens with a full drum groove: kick, snare, and hi-hat. Jason traces the
            pattern: "It's not kick, snare, kick, kick, snare, kick — there's a hi-hat somewhere
            in the middle." Then, partway through, the drums stop and the <strong>tambourine</strong>
            enters. "The tambourine. So that's adding a lot of value." The tambourine replaces the
            full drum kit, thinning the texture dramatically.
          </p>
          <p>
            Then the drummer switches to the <strong>ride cymbal</strong>: "Earlier it was hi-hats,
            now he's gone to probably the right side — he's playing the ride cymbal." The ride
            has a more sustained, shimmering sound than the hi-hat. Each of these switches —
            hi-hat, tambourine, ride — is planned. "These older composers put a lot of time into
            their parts."
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="Vocals" title="Lyrics and oohs — how the OO transitions the harmony">
        <Prose>
          <p>
            On the right channel, "In My Life" has two vocal lines. The main vocal carries the
            lyric. The secondary voice holds a sustained OO on the off-lines, alternating with
            the lyric. "So there's lyrics and oohs. So try to toggle between that. So that's a very
            interesting way of writing it."
          </p>
          <p>
            The OO isn't just decoration. At the harmonic turn where the song moves from D major
            to D minor, the OO vowel is what the secondary voice uses — and Jason explains exactly
            why: "That OO sounds really good there." It helps the voices move from the D major
            chord to D minor. The rounded, covered vowel sound softens the shift between the two
            chords, making it feel like a gentle slide rather than an abrupt change.
          </p>
          <p>
            This moment — D major to D minor going to A major — is a <strong>plagal cadence</strong>:
            the 4 chord (D) going to the 1 chord (A). "So this is a plagal cadence — D, D minor,
            and going to A. This whole thing you need to group together as a plagal cadence."
            The oohs are "reserved for this major-minor plagal" specifically.
          </p>
        </Prose>

        <RomanNumeralAnalysis
          label="In My Life — verse chord progression (A major)"
          caption="A → E7 → F♯m → A7/G → D → Dm → A, as worked out in class (Jason's handwritten chart writes the second chord as A/E). The A7 (with G, its seventh, in the bass) functions as a secondary dominant pointing toward D. The D to Dm shift (parallel minor) followed by a return to A is the plagal cadence — the emotional heart of the song."
          chords={[
            { chord: "A", rn: "I", function: "Tonic" },
            { chord: "E7", rn: "V7", function: "Dominant" },
            { chord: "F#m", rn: "vi", function: "Tonic" },
            { chord: "A7/G", rn: "V⁴₂/IV", function: "Secondary V" },
            { chord: "D", rn: "IV", function: "Pre-dominant" },
            { chord: "Dm", rn: "iv", function: "Pre-dominant" },
            { chord: "A", rn: "I", function: "Tonic" },
          ]}
        />

        <TheoryBox label="Using Moises for In My Life">
          Import a high-quality copy of the song: "There's an album on Spotify called Remastered
          Beatles. There's either remastered 2009 or something like that. One of those albums are
          very high quality. That's what I'm playing right now." The better the audio, the better
          the stem separation. Turn off the chord display in Moises settings. Use the bass isolation to confirm each chord root, and use the vocal
          isolation to hear the OO harmony separately from the lyric.
        </TheoryBox>
      </Chapter>

      <Chapter eyebrow="Solo piano arrangement" title="You don't need to play chords in the left hand">
        <Prose>
          <p>
            For students building a solo piano arrangement of "In My Life," Jason makes a
            counterintuitive point: "You don't need to play chords if you're doing a solo piano
            arrangement for this song, because the vocal harmony covers it in the right hand."
          </p>
          <p>
            The right hand handles the melody and, optionally, the vocal harmonies. The left hand
            can then focus on the bass line and rhythmic groove rather than spelling out every chord
            in full. "If you were going to play this song on piano, would you try to replicate the
            drums or the bass? I would do the bass more than the drums, and then do a bit of both.
            I'll try and first learn the bass." The bass line is where the musical motion lives.
          </p>
          <p>
            The piano solo — written and performed by producer George Martin — is a separate study.
            Jason has notated it and shares the notation with students. "It's rather tricky," he
            warns — to him it sounds like they are trying to copy Johann Sebastian Bach in this
            part. Martin recorded the solo on piano at half-speed,
            so when played back at normal speed, the pitch and tempo both double, giving it the
            sound and character of a Baroque harpsichord. The left hand of the solo moves in its
            own independent line while the right hand runs scales and ornaments.
          </p>
        </Prose>

        <NoteImage
          src="/notes/the-beatles/finals/08-in-my-life.png"
          caption="In My Life — verse chord map and intro melody notation. The two intro lines (high guitar and bass) are shown on separate staves. The D to Dm plagal cadence is marked with a bracket. The George Martin piano solo is notated separately."
        />
      </Chapter>
    </LessonShell>
  );
}
